/**
 * API Client
 * 
 * Centralized Axios instance with interceptors for:
 * - Authentication (adds Bearer token to all requests)
 * - Error handling (401 redirects, network errors)
 * - Request/response logging (for debugging)
 * 
 * Benefits:
 * - Single place to configure all HTTP requests
 * - Automatic token injection
 * - Consistent error handling
 * - Easy to add features (retry logic, rate limiting, etc.)
 */

import axios from 'axios';

// ============================================================================
// BASE CONFIGURATION
// ============================================================================

/**
 * Base URL for API requests
 * Comes from environment variable (set in .env file)
 * Falls back to localhost for development
 */
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Create Axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

// Auth0 token getter - will be set by the app
let getAccessTokenSilently = null;

/**
 * Set the Auth0 token getter function
 * This is called from the main app after Auth0 is initialized
 */
export const setAuth0TokenGetter = (tokenGetter) => {
  getAccessTokenSilently = tokenGetter;
};

/**
 * Request Interceptor
 * 
 * Runs before every request is sent
 * - Adds Auth0 authentication token to headers
 * - Logs request details (in development)
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Get Auth0 token if available
      if (getAccessTokenSilently) {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: 'openid profile email'
          },
          cacheMode: 'cache-only' // Don't force refresh on every request
        });
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      console.error('Failed to get Auth0 access token:', error);
      // Continue with request without token - let backend handle 401
    }

    // Log request in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    // Handle request errors (rare, usually network issues)
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

/**
 * Response Interceptor
 * 
 * Runs after every response is received
 * - Logs response details (in development)
 * - Handles common errors (401, 403, 500, etc.)
 * - Extracts data from response
 */
apiClient.interceptors.response.use(
  (response) => {
    // Log response in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }

    // Return the response as-is
    // Components can access response.data
    return response;
  },
  (error) => {
    // Handle response errors
    return handleResponseError(error);
  }
);

// ============================================================================
// ERROR HANDLER
// ============================================================================

/**
 * Handle API response errors
 * Provides consistent error handling across the app
 * 
 * @param {Error} error - Axios error object
 * @returns {Promise} Rejected promise with formatted error
 */
const handleResponseError = (error) => {
  // Log error in development
  if (process.env.NODE_ENV === 'development') {
    console.error('❌ API Error:', error);
  }

  // No response received (network error, timeout, etc.)
  if (!error.response) {
    const networkError = {
      message: 'Network error. Please check your internet connection.',
      code: 'NETWORK_ERROR',
      originalError: error,
    };

    console.error('Network Error:', error.message);
    return Promise.reject(networkError);
  }

  // Server responded with error status
  const { status, data } = error.response;

  // Extract error message from various possible formats
  const errorMessage = data?.message || data?.error?.message || data?.error || 'An unexpected error occurred.';

  // Handle specific status codes
  switch (status) {
    case 401:
      // Unauthorized - token expired or invalid
      handleUnauthorized();
      return Promise.reject({
        message: errorMessage || 'Your session has expired. Please log in again.',
        code: data?.code || 'UNAUTHORIZED',
        status: 401,
      });

    case 403:
      // Forbidden - user doesn't have permission
      return Promise.reject({
        message: errorMessage || 'You do not have permission to perform this action.',
        code: data?.code || 'FORBIDDEN',
        status: 403,
      });

    case 404:
      // Not found
      return Promise.reject({
        message: errorMessage || 'The requested resource was not found.',
        code: data?.code || 'NOT_FOUND',
        status: 404,
      });

    case 422:
      // Validation error
      return Promise.reject({
        message: errorMessage || 'Validation error. Please check your input.',
        code: data?.code || 'VALIDATION_ERROR',
        status: 422,
        errors: data?.errors || {}, // Field-specific errors
      });

    case 429:
      // Too many requests (rate limiting)
      return Promise.reject({
        message: errorMessage || 'Too many requests. Please try again later.',
        code: data?.code || 'RATE_LIMIT',
        status: 429,
      });

    case 500:
    case 502:
    case 503:
    case 504:
      // Server errors
      return Promise.reject({
        message: errorMessage || 'Server error. Please try again later.',
        code: data?.code || 'SERVER_ERROR',
        status,
      });

    default:
      // Generic error
      return Promise.reject({
        message: errorMessage || 'An unexpected error occurred.',
        code: data?.code || 'UNKNOWN_ERROR',
        status,
      });
  }
};

/**
 * Handle 401 Unauthorized errors
 * With Auth0, we don't need to clear localStorage
 * The Auth0 provider will handle the redirect to login
 */
const handleUnauthorized = () => {
  // With Auth0, the authentication state is managed by the Auth0Provider
  // A 401 response means the token is invalid/expired
  // The user will be automatically redirected to login by the Auth0Provider
  console.warn('401 Unauthorized - Auth0 will handle re-authentication');
};

// ============================================================================
// HELPER METHODS
// ============================================================================

/**
 * GET request helper
 * 
 * @param {string} url - API endpoint
 * @param {object} config - Axios config (optional)
 * @returns {Promise} Response data
 * 
 * @example
 * const data = await apiClient.get('/api/calls/123');
 */
apiClient.get = async (url, config = {}) => {
  try {
    const response = await apiClient.request({
      method: 'GET',
      url,
      ...config,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * POST request helper
 * 
 * @param {string} url - API endpoint
 * @param {object} data - Request body
 * @param {object} config - Axios config (optional)
 * @returns {Promise} Response data
 * 
 * @example
 * const data = await apiClient.post('/api/calls/list', { limit: 50 });
 */
apiClient.post = async (url, data = {}, config = {}) => {
  try {
    const response = await apiClient.request({
      method: 'POST',
      url,
      data,
      ...config,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * PUT request helper
 * 
 * @param {string} url - API endpoint
 * @param {object} data - Request body
 * @param {object} config - Axios config (optional)
 * @returns {Promise} Response data
 */
apiClient.put = async (url, data = {}, config = {}) => {
  try {
    const response = await apiClient.request({
      method: 'PUT',
      url,
      data,
      ...config,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * PATCH request helper
 * 
 * @param {string} url - API endpoint
 * @param {object} data - Request body
 * @param {object} config - Axios config (optional)
 * @returns {Promise} Response data
 */
apiClient.patch = async (url, data = {}, config = {}) => {
  try {
    const response = await apiClient.request({
      method: 'PATCH',
      url,
      data,
      ...config,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * DELETE request helper
 * 
 * @param {string} url - API endpoint
 * @param {object} config - Axios config (optional)
 * @returns {Promise} Response data
 */
apiClient.delete = async (url, config = {}) => {
  try {
    const response = await apiClient.request({
      method: 'DELETE',
      url,
      ...config,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============================================================================
// EXPORT
// ============================================================================

export default apiClient;

